import { useState, useEffect } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import apiClient from "@/utils/apiClient";
import { Link2, Copy, Check, Users, DollarSign, TrendingUp, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Affiliate() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ platform: "", audience_size: "", reason: "" });

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await apiClient.get('/affiliates/my');
        setData(res.data);
      } catch (err) { /* silent */ }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleApply = async (e) => {
    e.preventDefault();
    setApplying(true);
    try {
      const res = await apiClient.post('/affiliates/apply', form);
      const myRes = await apiClient.get('/affiliates/my');
      setData(myRes.data);
    } catch (err) { /* silent */ }
    finally { setApplying(false); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(data.affiliate.referral_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-spa-terracotta border-t-transparent rounded-full animate-spin" /></div>
      </DashboardLayout>
    );
  }

  if (!data?.enrolled) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid="affiliate-apply">
          <div className="mb-8 text-center">
            <p className="overline-text mb-2">Partner With Us</p>
            <h1 className="font-serif text-3xl sm:text-4xl font-medium text-spa-moss mb-3">SMAHOO ME.WELL Affiliate Program</h1>
            <p className="text-spa-text-secondary max-w-xl mx-auto">Earn commissions by sharing the gift of wellness. Refer friends and followers to Mind-SPA and earn on every subscription.</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="spa-card text-center">
              <Link2 className="w-8 h-8 text-spa-terracotta mx-auto mb-3" />
              <h3 className="font-medium text-spa-moss mb-1">Share</h3>
              <p className="text-spa-text-secondary text-sm">Get your unique referral code</p>
            </div>
            <div className="spa-card text-center">
              <Users className="w-8 h-8 text-spa-sage mx-auto mb-3" />
              <h3 className="font-medium text-spa-moss mb-1">Refer</h3>
              <p className="text-spa-text-secondary text-sm">Friends sign up through your link</p>
            </div>
            <div className="spa-card text-center">
              <DollarSign className="w-8 h-8 text-spa-sky mx-auto mb-3" />
              <h3 className="font-medium text-spa-moss mb-1">Earn</h3>
              <p className="text-spa-text-secondary text-sm">Earn commission on subscriptions</p>
            </div>
          </div>

          <form onSubmit={handleApply} className="spa-card space-y-4" data-testid="affiliate-form">
            <h2 className="font-serif text-xl text-spa-moss">Apply Now</h2>
            <Input value={form.platform} onChange={(e) => setForm({...form, platform: e.target.value})} placeholder="Your platform (Instagram, YouTube, Blog...)" className="spa-input" required data-testid="aff-platform" />
            <Input value={form.audience_size} onChange={(e) => setForm({...form, audience_size: e.target.value})} placeholder="Audience size (approximate)" className="spa-input" required data-testid="aff-audience" />
            <textarea value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} placeholder="Why you'd be a great SMAHOO ME.WELL affiliate..." className="w-full spa-input min-h-[100px] resize-none p-3" required data-testid="aff-reason" />
            <Button type="submit" disabled={applying} className="spa-button-primary w-full" data-testid="aff-submit">
              {applying ? "Submitting..." : "Submit Application"}
            </Button>
          </form>
        </div>
      </DashboardLayout>
    );
  }

  const aff = data.affiliate;
  const statusColor = aff.status === "approved" ? "bg-emerald-100 text-emerald-700" : aff.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="affiliate-dashboard">
        <div className="mb-8">
          <p className="overline-text mb-2">Your Affiliate Dashboard</p>
          <h1 className="font-serif text-3xl font-medium text-spa-moss mb-2">SMAHOO ME.WELL Affiliate</h1>
          <span className={`px-3 py-1 rounded-full text-sm ${statusColor}`}>{aff.status}</span>
        </div>

        {/* Referral Code */}
        <div className="spa-card mb-6">
          <p className="text-spa-text-secondary text-sm mb-2">Your Referral Code</p>
          <div className="flex items-center gap-3">
            <code className="flex-1 bg-spa-muted px-4 py-3 rounded-xl font-mono text-spa-moss text-lg" data-testid="referral-code">{aff.referral_code}</code>
            <Button onClick={copyCode} variant="outline" className="border-spa-border rounded-xl gap-2" data-testid="copy-code">
              {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-4 gap-4 mb-8">
          <div className="spa-card text-center">
            <TrendingUp className="w-6 h-6 text-spa-terracotta mx-auto mb-2" />
            <p className="font-serif text-2xl text-spa-moss">{aff.commission_rate}%</p>
            <p className="text-spa-text-secondary text-xs">Commission Rate</p>
          </div>
          <div className="spa-card text-center">
            <Users className="w-6 h-6 text-spa-sage mx-auto mb-2" />
            <p className="font-serif text-2xl text-spa-moss">{aff.total_referrals}</p>
            <p className="text-spa-text-secondary text-xs">Total Referrals</p>
          </div>
          <div className="spa-card text-center">
            <DollarSign className="w-6 h-6 text-spa-sky mx-auto mb-2" />
            <p className="font-serif text-2xl text-spa-moss">CA${aff.total_earnings.toFixed(2)}</p>
            <p className="text-spa-text-secondary text-xs">Total Earnings</p>
          </div>
          <div className="spa-card text-center">
            <DollarSign className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
            <p className="font-serif text-2xl text-spa-moss">CA${aff.total_paid.toFixed(2)}</p>
            <p className="text-spa-text-secondary text-xs">Total Paid Out</p>
          </div>
        </div>

        {/* Referrals */}
        <h2 className="font-serif text-xl text-spa-moss mb-4">Recent Referrals</h2>
        {data.referrals?.length > 0 ? (
          <div className="spa-card overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-spa-text-secondary text-xs uppercase border-b border-spa-border">
                <th className="text-left p-3">Referred User</th><th className="text-left p-3">Date</th><th className="text-left p-3">Status</th><th className="text-left p-3">Earnings</th>
              </tr></thead>
              <tbody>
                {data.referrals.map((r, i) => (
                  <tr key={r.referred_email || `ref-${i}`} className="border-b border-spa-border last:border-0">
                    <td className="p-3 text-spa-moss">{r.referred_name || r.referred_email}</td>
                    <td className="p-3 text-spa-text-secondary">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="p-3"><span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs">{r.status}</span></td>
                    <td className="p-3 text-spa-moss">CA${r.commission?.toFixed(2) || "0.00"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="spa-card p-8 text-center">
            <Clock className="w-8 h-8 text-spa-border mx-auto mb-3" />
            <p className="text-spa-text-secondary">No referrals yet. Share your code to start earning!</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
